"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Pagination,
  Breadcrumb,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { HomeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { apiRequest } from "../../../../service/apiRequest";
import { showSuccess, showError } from "../../../../components/errorHandler";
import { useCurrentModule } from "../../../../hooks/useCurrentModule";
import { useAppDispatch } from "../../../../redux/hooks";
import {
  fetchWoodData,
  fetchWoodTypeData,
  fetcProductData,
  fetcunittypeData,
} from "../../../../redux/slice/masterSlice";
import BomModal from "./BomModal";

const { Text } = Typography;

/* ---------------- TYPES ---------------- */

export interface BomData {
  bom_id: number;
  bom_product_id: number;
  bom_created_on: string;
  components: any[];
  Machinery: any[];
  Assembly: any[];
  Raw: any[];
  Sanding: any[];
  Finishing: any[];
  Packed: any[];
}

/* ---------------- COMPONENT ---------------- */

const BOM: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<BomData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BomData | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const currentModule = useCurrentModule();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  /* ---------------- FETCH DATA ---------------- */

  const fetchData = async () => {
    try {
      const res = await apiRequest<BomData[]>({
        method: "get",
        url: "get-bom", // ✅ your new API
      });
      setData(res || []);
    } catch {
      showError("Failed to fetch BOM data");
    }
  };

  useEffect(() => {
    fetchData();
    dispatch(fetchWoodData());
    dispatch(fetchWoodTypeData());
    dispatch(fetcProductData());
    dispatch(fetcunittypeData());
  }, [dispatch]);

  useEffect(() => {
    const pid = searchParams.get("pid");
    if (pid) {
      const pidNum = Number(pid);
      if (!isNaN(pidNum)) {
        setInitialValues({ bom_product_id: pidNum } as any);
        setIsEditMode(false);
        setIsModalVisible(true);
      }
    }
  }, [searchParams]);

  /* ---------------- MODAL ---------------- */

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const showEditModal = (record: BomData) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
    if (searchParams.has("pid")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("pid");
      setSearchParams(newParams);
    }
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async (id: number) => {
    try {
      await apiRequest({
        method: "delete",
        url: `delete-bom/${id}`, // ✅ your delete API
      });
      showSuccess("BOM deleted successfully");
      fetchData();
    } catch {
      showError("Failed to delete BOM");
    }
  };

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns: ColumnsType<BomData> = [
    {
      title: "BOM ID",
      dataIndex: "bom_id",
      key: "bom_id",
    },
    {
      title: "Product ID",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Components",
      key: "components",
      render: (_: any, record: BomData) =>
        record.components?.length || 0,
    },
    {
      title: "Created On",
      dataIndex: "bom_created_on",
      key: "bom_created_on",
      render: (value: string) =>
        dayjs(value).format("DD-MMM-YYYY HH:mm"),
    }
  ];

  /* ---------------- ACTION COLUMN ---------------- */

  if (currentModule?.right_edit === "Y" || currentModule?.right_delete === "Y") {
    columns.push({
      title: "Action",
      key: "action",
      render: (_: any, record: BomData) => (
        <Space>
          <Button onClick={() => showEditModal(record)}>
            <EditOutlined />
          </Button>

          <Popconfirm
            title="Delete?"
            onConfirm={() => handleDelete(record.bom_id)}
          >
            <Button danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <Space
        align="center"
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Bill of Material (B.O.M)</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === "Y" && (
          <Button type="primary" onClick={showCreateModal}>
            Add New BOM
          </Button>
        )}
      </Space>

      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
        <Table<BomData>
          columns={columns}
          dataSource={data.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          rowKey="bom_id"
          pagination={false}
          scroll={{ y: 500 }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, data.length)} of{" "}
                {data.length}
              </Text>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={data.length}
                showSizeChanger
                onChange={(page, pageSize) =>
                  setPagination({ current: page, pageSize })
                }
              />
            </div>
          )}
        />
      </div>

      <BomModal
        visible={isModalVisible}
        onCancel={hideModal}
        onCreate={fetchData}
        initialValues={initialValues} // ✅ important
        isEditMode={isEditMode}
      />
    </>
  );
};

export default BOM;